import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Upload, 
  Edit, 
  Trash2, 
  Plus, 
  Loader2,
  GraduationCap,
  MapPin,
  Calendar,
  Clock,
  User,
  X,
  Check,
  Paperclip,
  FileText
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const KursdetailsPage = () => {
  const [kurse, setKurse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingKurs, setEditingKurs] = useState(null);
  const [kursColors, setKursColors] = useState([]);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState('');
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [removeFile, setRemoveFile] = useState(false);
  const [textbausteineInput, setTextbausteineInput] = useState('');

  useEffect(() => {
    fetchKurse();
    fetchKursColors();
  }, []);

  const fetchKurse = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/api/admin/kurse`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKurse(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Kurse:', error);
      toast.error('Fehler beim Laden der Kurse');
    } finally {
      setLoading(false);
    }
  };

  const fetchKursColors = async () => {
    try {
      const response = await axios.get(`${API}/api/public/kurs-colors`);
      setKursColors(response.data.colors || []);
    } catch (error) {
      // Use defaults if not available
      setKursColors([
        { name: "Junior", color: "#fce7f3" },
        { name: "Bronze", color: "#dcfce7" },
        { name: "Silber", color: "#fed7aa" },
        { name: "Gold", color: "#dbeafe" },
      ]);
    }
  };

  const getKursColor = (kursName) => {
    if (!kursName) return '#ffffff';
    const kursLower = kursName.toLowerCase();
    for (const colorConfig of kursColors) {
      if (kursLower.includes(colorConfig.name.toLowerCase())) {
        return colorConfig.color;
      }
    }
    return '#ffffff'; // Default white
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/api/admin/kurse/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`${response.data.imported_count} Kurse importiert`);
      fetchKurse();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Import');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleEdit = (kurs) => {
    setEditingKurs({ ...kurs });
    setTextbausteineInput(kurs.textbausteine?.join(', ') || '');
    setSelectedFile(null);
    setRemoveFile(false);
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingKurs.kurs?.trim()) {
      toast.error('Kursname ist erforderlich');
      return;
    }

    // Konvertiere Textbausteine-Input zu Array
    const textbausteineArray = textbausteineInput
      .split(',')
      .map(t => t.trim().toUpperCase())
      .filter(t => t && /^[A-Z]$/.test(t));
    
    const kursData = {
      ...editingKurs,
      textbausteine: textbausteineArray
    };

    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingKurs.id) {
        // Update existing course
        await axios.put(
          `${API}/api/admin/kurse/${editingKurs.id}`,
          kursData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Handle file upload if new file selected
        if (selectedFile) {
          const fileFormData = new FormData();
          fileFormData.append('file', selectedFile);
          await axios.post(
            `${API}/api/admin/kurse/${editingKurs.id}/upload-file`,
            fileFormData,
            { 
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              } 
            }
          );
        }
        
        // Handle file removal
        if (removeFile && editingKurs.file_name) {
          await axios.delete(`${API}/api/admin/kurse/${editingKurs.id}/file`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        
        toast.success('Kurs aktualisiert');
      } else {
        // Create new course
        const response = await axios.post(
          `${API}/api/admin/kurse`,
          kursData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Upload file if selected
        if (selectedFile && response.data.id) {
          const fileFormData = new FormData();
          fileFormData.append('file', selectedFile);
          await axios.post(
            `${API}/api/admin/kurse/${response.data.id}/upload-file`,
            fileFormData,
            { 
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              } 
            }
          );
        }
        
        toast.success('Kurs erstellt');
      }

      setEditDialogOpen(false);
      setEditingKurs(null);
      setTextbausteineInput('');
      setSelectedFile(null);
      setRemoveFile(false);
      fetchKurse();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/api/admin/kurse/${deleteTarget}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kurs gelöscht');
      setDeleteTarget(null);
      fetchKurse();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleDeleteAll = async () => {
    if (deleteAllConfirm !== 'LÖSCHEN') {
      toast.error('Bitte "LÖSCHEN" eingeben zur Bestätigung');
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      const result = await axios.delete(`${API}/api/admin/kurse`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${result.data.deleted_count} Kurse gelöscht`);
      setShowDeleteAllDialog(false);
      setDeleteAllConfirm('');
      fetchKurse();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleNewKurs = () => {
    setEditingKurs({
      kurs: '',
      ort: '',
      tag: '',
      zeit: '',
      lehrer: '',
      telefonnummer: '',
      pruefungstermin: '',
      kursstart: '',
      einheiten: '',
      bloecke: '',
      adresse: '',
      textbausteine: []
    });
    setTextbausteineInput('');
    setSelectedFile(null);
    setRemoveFile(false);
    setEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header - Kompakter */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kursdetails</h1>
          <p className="text-sm text-gray-600">Kurse verwalten und bearbeiten</p>
        </div>
        <div className="flex gap-2">
          {kurse.length > 0 && (
            <Button 
              onClick={() => setShowDeleteAllDialog(true)}
              size="sm"
              variant="outline"
              className="text-red-600 hover:bg-red-50 border-red-200"
              data-testid="delete-all-kurse-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Alle löschen
            </Button>
          )}
          <Button 
            onClick={handleNewKurs} 
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="new-kurs-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Neuer Kurs
          </Button>
          <label>
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              className="hidden"
              disabled={uploading}
              data-testid="csv-upload-input"
            />
            <Button asChild disabled={uploading} variant="outline" size="sm" data-testid="csv-import-btn">
              <span className="cursor-pointer">
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-1" />
                    CSV Import
                  </>
                )}
              </span>
            </Button>
          </label>
        </div>
      </div>

      {/* Kurse Liste - Kompakter */}
      {kurse.length === 0 ? (
        <Card data-testid="no-kurse-card">
          <CardContent className="py-8 text-center">
            <GraduationCap className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">Keine Kurse vorhanden</h3>
            <p className="text-sm text-gray-600 mb-3">Erstelle einen neuen Kurs oder importiere via CSV</p>
            <Button onClick={handleNewKurs} size="sm" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-1" />
              Ersten Kurs erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kurse-grid">
          {kurse.map(kurs => (
            <Card 
              key={kurs.id} 
              className="hover:shadow-md transition-shadow border-l-4"
              style={{ 
                borderLeftColor: getKursColor(kurs.kurs),
                backgroundColor: `${getKursColor(kurs.kurs)}10`
              }}
              data-testid={`kurs-card-${kurs.id}`}
            >
              <CardHeader className="py-4 px-5">
                <CardTitle className="flex items-center justify-between text-base">
                  <span className="font-semibold truncate">{kurs.kurs}</span>
                  <div className="flex gap-1 ml-2 flex-shrink-0">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={() => handleEdit(kurs)}
                      data-testid={`edit-kurs-${kurs.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setDeleteTarget(kurs.id)}
                      data-testid={`delete-kurs-${kurs.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="py-3 px-5 space-y-2 text-sm">
                {kurs.lehrer && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="truncate">{kurs.lehrer}</span>
                  </div>
                )}
                {kurs.ort && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span className="truncate">{kurs.ort}</span>
                  </div>
                )}
                {(kurs.tag || kurs.zeit) && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span>{[kurs.tag, kurs.zeit].filter(Boolean).join(', ')}</span>
                  </div>
                )}
                {kurs.file_name && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <Paperclip className="h-4 w-4" />
                    <span className="truncate">{kurs.file_name}</span>
                  </div>
                )}
                {kurs.textbausteine && kurs.textbausteine.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {kurs.textbausteine.map((tb, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs px-2 py-0.5">
                        {tb}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      {editDialogOpen && editingKurs && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-kurs-dialog">
            <CardHeader className="py-4 px-6 border-b flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-xl">{editingKurs.id ? 'Kurs bearbeiten' : 'Neuen Kurs erstellen'}</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {editingKurs.id ? `Bearbeite "${editingKurs.kurs}"` : 'Fülle die Kursdetails aus'}
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => { setEditDialogOpen(false); setEditingKurs(null); setSelectedFile(null); setRemoveFile(false); setTextbausteineInput(''); }}
              >
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Kursname *</Label>
                  <Input
                    value={editingKurs.kurs || ''}
                    onChange={e => setEditingKurs({...editingKurs, kurs: e.target.value})}
                    placeholder="z.B. Bronze 1"
                    className="h-10 text-base mt-1"
                    data-testid="kurs-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Ort</Label>
                  <Input
                    value={editingKurs.ort || ''}
                    onChange={e => setEditingKurs({...editingKurs, ort: e.target.value})}
                    className="h-10 text-base mt-1"
                    data-testid="kurs-ort-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Tag</Label>
                  <Input
                    value={editingKurs.tag || ''}
                    onChange={e => setEditingKurs({...editingKurs, tag: e.target.value})}
                    placeholder="z.B. Montag"
                    className="h-10 text-base mt-1"
                    data-testid="kurs-tag-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Zeit</Label>
                  <Input
                    value={editingKurs.zeit || ''}
                    onChange={e => setEditingKurs({...editingKurs, zeit: e.target.value})}
                    placeholder="z.B. 16:00"
                    className="h-10 text-base mt-1"
                    data-testid="kurs-zeit-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Lehrer</Label>
                  <Input
                    value={editingKurs.lehrer || ''}
                    onChange={e => setEditingKurs({...editingKurs, lehrer: e.target.value})}
                    className="h-10 text-base mt-1"
                    data-testid="kurs-lehrer-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Telefonnummer</Label>
                  <Input
                    value={editingKurs.telefonnummer || ''}
                    onChange={e => setEditingKurs({...editingKurs, telefonnummer: e.target.value})}
                    className="h-10 text-base mt-1"
                    data-testid="kurs-telefon-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Kursstart</Label>
                  <Input
                    value={editingKurs.kursstart || ''}
                    onChange={e => setEditingKurs({...editingKurs, kursstart: e.target.value})}
                    className="h-10 text-base mt-1"
                    data-testid="kurs-start-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Prüfungstermin</Label>
                  <Input
                    value={editingKurs.pruefungstermin || ''}
                    onChange={e => setEditingKurs({...editingKurs, pruefungstermin: e.target.value})}
                    className="h-10 text-base mt-1"
                    data-testid="kurs-pruefung-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Adresse</Label>
                <Input
                  value={editingKurs.adresse || ''}
                  onChange={e => setEditingKurs({...editingKurs, adresse: e.target.value})}
                  className="h-10 text-base mt-1"
                  data-testid="kurs-adresse-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Textbausteine (z.B. A, B, C)</Label>
                <Input
                  value={textbausteineInput}
                  onChange={e => setTextbausteineInput(e.target.value)}
                  placeholder="A, B, C"
                  className="h-10 text-base mt-1"
                  data-testid="kurs-textbausteine-input"
                />
                <p className="text-xs text-gray-500 mt-1">Buchstaben A-Z, mit Komma getrennt</p>
              </div>

              {/* Dateianhang Sektion */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                  <Paperclip className="h-4 w-4" />
                  <span>Terminliste</span>
                  <span className="text-xs font-normal text-gray-500">(optional - wird im Dashboard angezeigt)</span>
                </div>
                
                {/* Existing file display */}
                {editingKurs.file_name && !removeFile && !selectedFile && (
                  <div className="p-3 rounded-lg border-2 border-green-200 bg-green-50 mb-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-green-900 truncate">{editingKurs.file_name}</p>
                          <p className="text-xs text-green-700">Aktuell hochgeladen</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setRemoveFile(true)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-100 flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* File upload area */}
                <label 
                  htmlFor="kurs-file" 
                  className="block p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 transition-colors cursor-pointer bg-gray-50 hover:bg-blue-50"
                >
                  <div className="text-center">
                    <Upload className="mx-auto h-8 w-8 text-gray-400" />
                    <p className="mt-1 text-sm font-medium text-gray-700">
                      {selectedFile ? selectedFile.name : 'Datei auswählen'}
                    </p>
                    <p className="text-xs text-gray-500">PDF, Bilder, Dokumente</p>
                  </div>
                </label>
                <input
                  id="kurs-file"
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    setSelectedFile(e.target.files[0]);
                    setRemoveFile(false);
                  }}
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => { setEditDialogOpen(false); setEditingKurs(null); setSelectedFile(null); setRemoveFile(false); setTextbausteineInput(''); }}
                  data-testid="cancel-kurs-btn"
                >
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleSave} 
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-kurs-btn"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Speichern
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-kurs-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kurs wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Kurs wird dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Confirmation */}
      <AlertDialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alle Kurse löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Es werden alle {kurse.length} Kurse unwiderruflich gelöscht. Tippe &quot;LÖSCHEN&quot; zur Bestätigung.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteAllConfirm}
            onChange={(e) => setDeleteAllConfirm(e.target.value)}
            placeholder='Tippe "LÖSCHEN"'
            className="my-2"
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteAllConfirm('')}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAll}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteAllConfirm !== 'LÖSCHEN'}
            >
              Alle löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default KursdetailsPage;
